import React, { useState, useEffect } from 'react';
import { Modal } from '@material-ui/core';
import { useForm, useController } from 'react-hook-form';
import { toastFlashMessage } from '@/utils';
import {
  BoxV2 as Box,
  Button,
  Flex,
  TextInput,
  MultiSelector,
} from 'portal-commons';
import { SelectItem } from '@/utils/types';
import { campaignController, RejectCampaignParams } from '@/apis/core';
import { faRectangleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CnpCampaignAction } from '../../enums';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '../../validators';
import { BulkOperationError } from '../../errors';

interface Props {
  open: boolean;
  campaignUids: string[];
  categories: SelectItem[];
  onClose: (reload: boolean) => void;
  onError: (error: BulkOperationError) => void;
}

interface FormValues {
  explanation: string;
  rejectionCategory: string[];
}

const BulkDeclineModalV2: React.FC<Props> = ({
  open,
  campaignUids,
  categories = [],
  onClose,
  onError,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
      rejectionCategory: [],
    },
  });

  const {
    field: { value: category, onChange: setCategory },
    fieldState: { error: categoryError },
  } = useController({
    control,
    name: 'rejectionCategory',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a category'),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Explanation'),
        isValidMaxLength: createMaxLengthValidator(2048),
        isValidInput,
      },
    },
  });

  const disabledSubmit =
    isProcessing || Object.values(errors).some((error) => !!error?.message);

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);
    try {
      const response = await campaignController.bulkRejectSharedCampaigns(
        campaignUids,
        data as RejectCampaignParams
      );
      if (response) {
        toastFlashMessage(
          `Success: ${campaignUids.length} campaign(s) declined`,
          'success'
        );
        onClose(true);
      }
    } catch (error) {
      onError?.(error as BulkOperationError);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} data-testid="bulkDeclineModal">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 525,
          minHeight: 200,
          bg: '#FFF',
          p: '24px 32px 8px',
          borderRadius: 10,
          '&:focus-visible': { outline: 'none' },
        }}
        data-testid="bulkDeclineModalContainer"
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            rowGap: 'xs',
          }}
        />

        <Box
          as="h3"
          sx={{
            py: 's',
            fontSize: 'H600',
            fontWeight: 600,
            lineHeight: '28px',
            color: 't.black80',
            textAlign: 'center',
            mb: 'xs',
          }}
        >
          <FontAwesomeIcon icon={faRectangleXmark} />
          <Box as="span" sx={{ ml: 's' }}>
            Decline Campaigns
          </Box>
        </Box>
        <form noValidate onSubmit={handleSubmit(onValidHandler)}>
          <Box as="p" sx={{ pt: 7, pb: 11, color: 't.black80', mb: 8 }}>
            Please confirm that you are declining{' '}
            <strong>
              {campaignUids.length}
              campaign(s)
            </strong>
            .
            <br />
            <br />
            Campaign ID(s):
            <br />
            <Box as="span" sx={{ fontWeight: 600 }}>
              {campaignUids.join(', ')}
            </Box>
          </Box>
          <Flex
            sx={{
              flexDirection: 'column',
              rowGap: '12px',
              color: '#333E41',
              mb: 'xs',
            }}
          >
            <Box as="span" sx={{ fontSize: '10px', lineHeight: '12px' }}>
              *Required Field
            </Box>
            <Box sx={{ width: '100%' }}>
              <MultiSelector
                disabled={isProcessing}
                options={categories}
                data-testid="rejectCategory"
                label="Category *"
                placeholder="Category"
                value={category[0]}
                error={!!categoryError?.message}
                helperText={categoryError?.message}
                onChange={(value: string) => {
                  if (value.length) {
                    const categoryArray = value.split(',');
                    setCategory(categoryArray);
                  } else {
                    setCategory([]);
                  }
                }}
              />
            </Box>
            <TextInput
              required
              data-testid="rejectExplanation"
              label="Explanation"
              placeholder="Please enter Explanation"
              disabled={isProcessing}
              value={explanation}
              maxLength={2048}
              error={!!explanationError?.message}
              note={explanationError?.message}
              onChange={setExplanation}
              onBlur={validateExplanation}
            />
          </Flex>
          <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
            <Button
              variant="outline"
              disabled={isProcessing}
              data-testid="rejectCampaignCancelButton"
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={disabledSubmit}
              data-testid="rejectCampaignDeclineButton"
              type="submit"
            >
              Decline
            </Button>
          </Flex>
        </form>
      </Box>
    </Modal>
  );
};

export default BulkDeclineModalV2;
